import React from 'react';
import { mui } from '@local/finsera-core/src/libs';

import { useMaxItemsPerRow } from './use-max-items-per-row';
import { FONT_FAMILY, FONT_SIZE } from '../../../../theme/layout-size';

type LegendRenderProps = {
  seriesColorByName: Record<string, string>;
  seriesKeys: string[];
  hiddenSeries: Set<string>;
  allSeriesHidden: boolean;
  onSeriesClick: (_seriesName: string) => void;
  onToggleAll: () => void;
};

export const LegendRender = ({
  seriesColorByName,
  seriesKeys,
  hiddenSeries,
  allSeriesHidden,
  onSeriesClick,
  onToggleAll,
}: LegendRenderProps) => {
  const shouldCenterProp = seriesKeys.length < 4;
  const showToggleAll = seriesKeys.length > 4;

  const containerRef = React.useRef<HTMLDivElement>(null);
  const columnGap = 10;
  const { maxItemsPerRow, isReady, shouldCenter } = useMaxItemsPerRow(containerRef, seriesKeys, columnGap, {
    shouldCenter: shouldCenterProp,
    showToggleAll,
  });

  return (
    <mui.core.Box
      ref={containerRef}
      sx={{
        width: '100%',
        maxHeight: 100,
        overflowY: 'auto',
        px: 8,
        py: 2,
      }}
    >
      {isReady && (
        <mui.core.Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <mui.core.Box
            sx={{
              display: 'grid',

              gridTemplateColumns: `repeat(${maxItemsPerRow}, max-content)`,
              columnGap: columnGap,
              rowGap: 0,
            }}
          >
            {seriesKeys.map((seriesName) => {
              const color = seriesColorByName[seriesName] || '#000000';
              return (
                <mui.core.Box
                  key={seriesName}
                  sx={{
                    display: 'flex',
                    justifyContent: shouldCenter ? 'center' : 'flex-start',
                    alignItems: 'center',
                  }}
                >
                  <mui.core.Box
                    onClick={() => onSeriesClick(seriesName)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      padding: 0.5,
                      cursor: 'pointer',
                      opacity: hiddenSeries.has(seriesName) ? 0.5 : 1,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        borderRadius: 1,
                      },
                    }}
                  >
                    <mui.core.Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: color,
                        borderRadius: 0,
                        flexShrink: 0,
                      }}
                    />
                    <mui.core.Typography
                      sx={{
                        fontSize: FONT_SIZE.xxs,
                        fontFamily: FONT_FAMILY,
                        color: 'text.primary',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {seriesName}
                    </mui.core.Typography>
                  </mui.core.Box>
                </mui.core.Box>
              );
            })}
            {showToggleAll && (
              <mui.core.Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}
              >
                <mui.core.Box
                  onClick={onToggleAll}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    padding: 0.5,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      borderRadius: 1,
                    },
                  }}
                >
                  {allSeriesHidden ? (
                    <mui.icons.VisibilityRounded
                      sx={{
                        width: 12,
                        height: 12,
                        fontSize: 12,
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <mui.icons.VisibilityOffRounded
                      sx={{
                        width: 12,
                        height: 12,
                        fontSize: 12,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <mui.core.Typography
                    sx={{
                      fontSize: FONT_SIZE.xxs,
                      fontFamily: FONT_FAMILY,
                      color: 'text.primary',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {allSeriesHidden ? 'Show All' : 'Hide All'}
                  </mui.core.Typography>
                </mui.core.Box>
              </mui.core.Box>
            )}
          </mui.core.Box>
        </mui.core.Box>
      )}
    </mui.core.Box>
  );
};
